import { useState } from 'react';
import { Icons } from '@/utils/Icons';
import { useLazyGetMatchesQuery, useLazyGetSportCompetitionsQuery } from '@/store/api/sportsApi';
import { useCasinoGameLauncher } from '@/hooks/useCasinoGameLauncher';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getSportIcon } from '@/utils';
import { useAppSelector } from '@/hooks';

/* ================= TYPES ================= */

type TreeNode = {
  id: string;
  label: string;
  children?: TreeNode[];
  sportName?: string;
  seriesId?: string;
  path?: string;
  gameName?: string;
  gameId?: number;
  disabled?: boolean;
};

type SidebarProps = {
  isOpen: boolean;
  onToggle: () => void;
};

/* ================= COMPONENT ================= */

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
  const navigate = useNavigate();
  /* ---------- API ---------- */

  const [getCompetitions] = useLazyGetSportCompetitionsQuery();
  const [getMatches] = useLazyGetMatchesQuery();
  const { launchCasinoGame } = useCasinoGameLauncher();
  const [launchingId, setLaunchingId] = useState<string | null>(null);

  /* ---------- STATE ---------- */

  const [openSections] = useState({
    others: true,
    allSports: true,
  });

  // Track which sport node is open (only one at a time)
  const [activeSportId, setActiveSportId] = useState<string | null>(null);

  // Track which competition node is open (only one at a time)
  const [activeCompetitionId, setActiveCompetitionId] = useState<string | null>(null);

  const [sportCompetitions, setSportCompetitions] = useState<Record<string, TreeNode[]>>({});

  const [competitionMatches, setCompetitionMatches] = useState<Record<string, TreeNode[]>>({});

  // Desktop collapsed (icon-only) mode, toggled from the header
  const isCollapsed = useAppSelector((state) => state.ui.sidebarCollapsed);
  const [hoveredSportId, setHoveredSportId] = useState<string | null>(null);

  /* ================= STATIC TREES ================= */

  const sportsTree: TreeNode[] = [
    { id: 'cricket', label: 'Cricket' },
    { id: 'football', label: 'Football' },
    { id: 'tennis', label: 'Tennis' },
    { id: 'horse-racing', label: 'Horse Racing', path: '/all-sports/Horse Racing' },
    { id: 'greyhound-racing', label: 'Greyhound Racing', path: '/all-sports/Greyhound Racing' },
    { id: 'politics', label: 'Politics', path: '/all-sports/Politics' },
    { id: 'kabaddi', label: 'Kabaddi', path: '/all-sports/Kabaddi' },
    // { id: 'other', label: 'Other' },
    // { id: 'binary', label: 'Binary' },
    // { id: 'basketball', label: 'Basketball' },
    // { id: 'baseball', label: 'Baseball' },
    // { id: 'table-tennis', label: 'Table Tennis' },
    { id: 'casino', label: 'All Casino', path: '/casino' },
  ];

  /* ================= HELPERS ================= */

  /* ================= API TREE LOGIC ================= */

  const loadCompetitions = async (node: TreeNode) => {
    if (sportCompetitions[node.id]) return;

    const res = await getCompetitions(node.label).unwrap();

    const competitions =
      res?.competitions?.map((comp) => ({
        id: `comp-${comp.id}`,
        label: comp.name,
        sportName: node.label,
        seriesId: String(comp.id),
      })) || [];

    setSportCompetitions((prev) => ({
      ...prev,
      [node.id]: competitions,
    }));
  };

  const loadMatches = async (node: TreeNode) => {
    if (!node.seriesId || competitionMatches[node.seriesId]) return;

    const res = await getMatches({
      sportName: node.sportName?.toLowerCase(),
      seriesId: node.seriesId,
    }).unwrap();

    const matches =
      res?.fixture?.map((match) => ({
        id: String(match.id),
        label: match.name,
        sportName: match.sport,
      })) || [];

    setCompetitionMatches((prev) => ({
      ...prev,
      [node.seriesId!]: matches,
    }));
  };

  const toggleNode = async (node: TreeNode, level: number) => {
    // SPORT LEVEL (level 0) — only one sport open at a time
    if (level === 0) {
      const isAlreadyOpen = activeSportId === node.id;

      if (isAlreadyOpen) {
        // Close the sport and clear its active competition
        setActiveSportId(null);
        setActiveCompetitionId(null);
        return;
      }

      // Switch to a new sport — close previous competition too
      setActiveSportId(node.id);
      setActiveCompetitionId(null);

      // Fetch competitions only if not already loaded
      await loadCompetitions(node);

      return;
    }

    // COMPETITION LEVEL (level 1) — only one competition open at a time
    if (level === 1 && node.seriesId) {
      const isAlreadyOpen = activeCompetitionId === node.seriesId;

      if (isAlreadyOpen) {
        setActiveCompetitionId(null);
        return;
      }

      setActiveCompetitionId(node.seriesId);

      // Fetch matches only if not already loaded
      await loadMatches(node);
    }
  };

  const handleOthersClick = async (node: TreeNode) => {
    if (!node.gameId) {
      toast.error(`${node.label} is currently unavailable.`, { id: 'sidebar-game-missing' });
      return;
    }

    setLaunchingId(node.id);
    try {
      await launchCasinoGame(node.gameId, 'mac88');
    } catch {
      toast.error('Failed to launch game', { id: 'sidebar-game-launch-error' });
    } finally {
      setLaunchingId(null);
    }
  };

  /* ================= TREE ITEM ================= */

  const TreeItem = ({
    node,
    level = 0,
    section,
  }: {
    node: TreeNode;
    level?: number;
    section: 'others' | 'allSports';
  }) => {
    const isOpen =
      level === 0
        ? activeSportId === node.id
        : level === 1
          ? activeCompetitionId === node.seriesId
          : false;

    const dynamicChildren =
      level === 0
        ? sportCompetitions[node.id]
        : level === 1 && node.seriesId
          ? competitionMatches[node.seriesId]
          : undefined;

    const children = dynamicChildren || node.children;

    const showToggleIcon =
      section === 'allSports' && !node.disabled && (level === 0 || level === 1);
    const paddingLeft = level === 0 ? 15 : level === 1 ? 25 : 35;

    const isLaunching = section === 'others' && launchingId === node.id;
    const isDisabled = Boolean(node.disabled || isLaunching);

    return (
      <div className="hover:bg-sidebar-item-border  hover:border-b">
        <button
          disabled={isDisabled}
          onClick={() => {
            if (node.disabled) return;

            if (node.path) {
              navigate(node.path);
              return;
            }

            if (section == 'others') {
              handleOthersClick(node);
              return;
            }
            if (level === 2 && node.sportName) {
              navigate(`/game-details/${node.sportName}/${node.id}`);
              return;
            }
            if (showToggleIcon) toggleNode(node, level);
          }}
          className={`group relative w-full text-start px-3 py-2.5 text-body-inverse transition-all duration-300 ease-in-out ${
            isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          } ${isOpen ? 'bg-white/8' : 'hover:bg-white/5'} ${level === 0 ? 'min-h-[48px]' : 'min-h-[34px]'}`}
          style={{ paddingLeft: `${paddingLeft + 8}px` }}
        >
          <span
            className={`absolute inset-x-3 bottom-0 h-px bg-white/30 transition-opacity duration-300 ${
              isOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
          />

          <div className="flex items-center gap-3">
            {section === 'allSports' && level === 0 && getSportIcon(node.label) ? (
              <div className="flex size-7 items-center justify-center overflow-hidden rounded-full">
                <img src={getSportIcon(node.label)!} alt="" className="size-5 object-contain" />
              </div>
            ) : (
              <span className="flex size-5 items-center justify-center text-white/80">
                <Icons.circle />
              </span>
            )}

            <span className="flex-1 text-[13px] font-medium text-white/90 hover:text-secondary-dark">
              {node.label}
            </span>

            {isLaunching && (
              <span className="size-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            )}

            {showToggleIcon && (
              <span
                className="flex-shrink-0 text-white/70 transition-transform duration-300 ease-in-out"
                style={{ transform: isOpen ? 'rotate(-90deg)' : 'rotate(0deg)' }}
              >
                <Icons.Back size={14} />
              </span>
            )}
          </div>
        </button>

        {isOpen && (
          <div className="transition-all duration-300 ease-in-out origin-top">
            {children?.map((child) => (
              <TreeItem key={child.id} node={child} level={level + 1} section={section} />
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ================= COLLAPSED RAIL (DESKTOP) ================= */

  // Rendered as a plain function (not a component) so hover state survives re-renders
  const renderCollapsedItem = (node: TreeNode) => {
    const icon = getSportIcon(node.label);
    const hasTree = !node.path;
    const isHovered = hoveredSportId === node.id;
    const competitions = sportCompetitions[node.id];

    return (
      <div
        key={node.id}
        className="relative"
        onMouseEnter={() => {
          setHoveredSportId(node.id);
          if (hasTree) loadCompetitions(node);
        }}
        onMouseLeave={() => {
          setHoveredSportId(null);
          setActiveCompetitionId(null);
        }}
      >
        <button
          type="button"
          aria-label={node.label}
          onClick={() => node.path && navigate(node.path)}
          className={`flex h-12 w-full items-center justify-center transition-colors ${
            isHovered ? 'bg-white/10' : 'hover:bg-white/5'
          }`}
        >
          {icon ? (
            <img src={icon} alt="" className="size-6 object-contain" />
          ) : (
            <span className="text-white/80">
              <Icons.circle />
            </span>
          )}
        </button>

        {isHovered && (
          <div className="absolute left-full top-0 z-40 w-[200px] bg-secondary shadow-xl">
            <button
              type="button"
              onClick={() => node.path && navigate(node.path)}
              className={`flex h-12 w-full items-center px-4 text-start text-[13px] font-semibold text-white ${
                hasTree ? 'border-b border-white/15 cursor-default' : 'cursor-pointer'
              }`}
            >
              {node.label}
            </button>

            {hasTree && (
              <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
                {!competitions && (
                  <div className="px-4 py-3 text-xs text-white/70">Loading...</div>
                )}
                {competitions?.length === 0 && (
                  <div className="px-4 py-3 text-xs text-white/70">No competitions</div>
                )}
                {competitions?.map((comp) => {
                  const isCompOpen = activeCompetitionId === comp.seriesId;
                  const matches = comp.seriesId ? competitionMatches[comp.seriesId] : undefined;

                  return (
                    <div key={comp.id} className="border-b border-white/10 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => toggleNode(comp, 1)}
                        className={`flex w-full items-center gap-2 px-4 py-2.5 text-start text-[13px] text-white/90 transition-colors ${
                          isCompOpen ? 'bg-white/10' : 'hover:bg-white/5'
                        }`}
                      >
                        <span className="flex size-4 shrink-0 items-center justify-center">
                          <Icons.circle />
                        </span>
                        <span className="flex-1">{comp.label}</span>
                        <span
                          className="shrink-0 text-white/70 transition-transform duration-300"
                          style={{ transform: isCompOpen ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                        >
                          <Icons.Back size={12} />
                        </span>
                      </button>

                      {isCompOpen && (
                        <div className="bg-black/10">
                          {!matches && (
                            <div className="px-6 py-2 text-xs text-white/70">Loading...</div>
                          )}
                          {matches?.length === 0 && (
                            <div className="px-6 py-2 text-xs text-white/70">No matches</div>
                          )}
                          {matches?.map((match) => (
                            <button
                              key={match.id}
                              type="button"
                              onClick={() =>
                                navigate(`/game-details/${match.sportName}/${match.id}`)
                              }
                              className="block w-full px-6 py-2 text-start text-xs text-white/85 hover:bg-white/5 hover:text-secondary-dark"
                            >
                              {match.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  /* ================= UI ================= */

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-x-0 top-[90px] bottom-0 bg-secondary/40 xl:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        className={`fixed xl:relative top-[90px] xl:top-0 right-0 h-[calc(100dvh-90px)] xl:h-full w-[240px] bg-primary border-l z-20 xl:z-30
    transform transition-all duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'} xl:translate-x-0 ${
      isCollapsed ? 'overflow-y-auto xl:overflow-visible xl:w-[60px]' : 'overflow-y-auto'
    }`}
      >
        {/* Full tree: mobile drawer always, desktop when expanded */}
        <div className={`text-sm ${isCollapsed ? 'xl:hidden' : ''}`}>
          {openSections.allSports &&
            sportsTree.map((node) => <TreeItem key={node.id} node={node} section="allSports" />)}
        </div>

        {/* Icon rail: desktop only, when collapsed */}
        {isCollapsed && (
          <div className="hidden xl:block pt-1">{sportsTree.map(renderCollapsedItem)}</div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;
